import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useBasket } from './BasketContext';

const BasketPageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 2rem;
  display: flex;
  gap: 2rem;
  align-items: flex-start;

  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const ItemsContainer = styled.div`
  flex: 2;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 2rem;
`;

const ItemCard = styled.div`
  display: flex;
  gap: 1.5rem;
  padding: 1.5rem 0;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }
`;

const ItemImage = styled.img`
  width: 120px;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
`;

const ItemDetails = styled.div`
  flex-grow: 1;
`;

const ItemName = styled.h3`
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
`;

const ItemPrice = styled.p`
  margin: 0;
  font-size: 1rem;
  color: #555;
`;

const ItemActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  input {
    width: 40px;
    text-align: center;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 5px;
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #005eb8;
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.9rem;
`;

const ShippingOptionsContainer = styled.div`
  margin: 1.5rem 0;
  padding-top: 1.5rem;
  border-top: 1px solid #ddd;
`;

const ShippingTitle = styled.h3`
  font-size: 1.1rem;
  margin-top: 0;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const ShippingOption = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }

  input[type="radio"] {
    margin-right: 0.75rem;
    accent-color: #005eb8;
    cursor: pointer;
  }

  label {
    cursor: pointer;
    flex-grow: 1;
    display: flex;
    justify-content: space-between;
  }
`;

const SummaryContainer = styled.div`
  flex: 1;
  background-color: #f8f9fa;
  padding: 2rem;
  border-radius: 8px;
  position: sticky;
  top: 120px; /* Adjust based on header height */
`;

const SummaryTitle = styled.h2`
  margin-top: 0;
  margin-bottom: 1.5rem;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  font-size: 1rem;

  &.total {
    font-weight: bold;
    font-size: 1.2rem;
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #ddd;
  }

  &.discount {
    color: #1a7a4c;
  }
`;

const DiscountSection = styled.div`
  margin: 1.5rem 0;
  padding-top: 1.5rem;
  border-top: 1px solid #ddd;
`;

const DiscountRow = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const DiscountInput = styled.input`
  flex: 1;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  text-transform: uppercase;
`;

const DiscountApplyButton = styled.button`
  padding: 10px 16px;
  background-color: #333;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background-color: #111; }
  &:disabled { background-color: #999; cursor: not-allowed; }
`;

const DiscountError = styled.p`
  color: #d9534f;
  font-size: 0.85rem;
  margin: 0.5rem 0 0;
`;

const AppliedDiscount = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #e4f5eb;
  padding: 0.6rem 0.9rem;
  border-radius: 4px;
  font-size: 0.9rem;
`;

const RemoveDiscountLink = styled.button`
  background: none;
  border: none;
  color: #005eb8;
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0;
`;

const CheckoutButton = styled(Link)`
  display: block;
  width: 100%;
  padding: 15px;
  background-color: #005eb8;
  color: white;
  text-align: center;
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: bold;
  border-radius: 4px;
  transition: background-color 0.3s;
  margin-top: 2rem;

  &:hover {
    background-color: #004a94;
  }

  @media (max-width: 992px) {
    box-sizing: border-box; /* Ensure padding is included in the width */
    width: auto; /* Allow it to shrink to content size + padding */
  }
`;

const Basket = () => {
  const {
    items, removeFromBasket, updateQuantity, clearBasket, shippingOption, setShippingOption,
    discount, discountStatus, discountError, applyDiscountCode, removeDiscountCode,
  } = useBasket();
  const [codeInput, setCodeInput] = React.useState('');

  const subtotal = items.reduce((acc, item) => {
    return acc + item.price * item.quantity;
  }, 0);

  // Recomputed from the live subtotal so the displayed amount always matches
  // the current basket, even if quantities change after a code is applied.
  // The real charge is still validated and computed server-side at checkout.
  const discountAmount = discount
    ? Math.min(discount.type === 'percent' ? subtotal * (discount.value / 100) : discount.value, subtotal)
    : 0;

  const shippingCost = shippingOption === 'post' && subtotal > 0 ? 2.99 : 0;
  const total = Math.max(0, subtotal - discountAmount) + shippingCost;

  const handleQuantityChange = (productId, newQuantity) => {
    const quantity = parseInt(newQuantity, 10);
    if (quantity >= 0) {
      updateQuantity(productId, quantity);
    }
  };

  const handleShippingChange = (event) => {
    setShippingOption(event.target.value);
  };

  const handleApplyDiscount = () => {
    applyDiscountCode(codeInput);
  };

  const handleRemoveDiscount = () => {
    removeDiscountCode();
    setCodeInput('');
  };

  if (items.length === 0) {
    return (
      <BasketPageContainer>
        <ItemsContainer>
          <PageTitle>Your Shopping Basket</PageTitle>
          <p>Your basket is empty.</p>
          <CheckoutButton to="/">Continue Shopping</CheckoutButton>
        </ItemsContainer>
      </BasketPageContainer>
    );
  }

  return (
    <BasketPageContainer>
      <ItemsContainer>
        <PageTitle>Your Shopping Basket</PageTitle>
        {items.map(item => (
          <ItemCard key={item.id}>
            <ItemImage src={item.image} alt={item.name} />
            <ItemDetails>
              <ItemName>{item.name}</ItemName>
              <ItemPrice>£{item.price.toFixed(2)}</ItemPrice>
            </ItemDetails>
            <ItemActions>
              <QuantitySelector>
                <span>Qty:</span>
                <input
                  type="number"
                  value={item.quantity}
                  onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                  min="0"
                />
              </QuantitySelector>
              <RemoveButton onClick={() => removeFromBasket(item.id)}>Remove</RemoveButton>
            </ItemActions>
          </ItemCard>
        ))}
      </ItemsContainer>
      <SummaryContainer>
        <SummaryTitle>Order Summary</SummaryTitle>
        <SummaryRow>
          <span>Subtotal</span>
          <span>£{subtotal.toFixed(2)}</span>
        </SummaryRow>
        {discount && (
          <SummaryRow className="discount">
            <span>Discount ({discount.code})</span>
            <span>&minus;£{discountAmount.toFixed(2)}</span>
          </SummaryRow>
        )}
        <DiscountSection>
          {discount ? (
            <AppliedDiscount>
              <span>Code <strong>{discount.code}</strong> applied</span>
              <RemoveDiscountLink onClick={handleRemoveDiscount}>Remove</RemoveDiscountLink>
            </AppliedDiscount>
          ) : (
            <>
              <DiscountRow>
                <DiscountInput
                  type="text"
                  placeholder="Discount code"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleApplyDiscount()}
                />
                <DiscountApplyButton onClick={handleApplyDiscount} disabled={discountStatus === 'checking' || !codeInput.trim()}>
                  {discountStatus === 'checking' ? 'Checking...' : 'Apply'}
                </DiscountApplyButton>
              </DiscountRow>
              {discountError && <DiscountError>{discountError}</DiscountError>}
            </>
          )}
        </DiscountSection>
        <ShippingOptionsContainer>
          <ShippingTitle>Shipping Options</ShippingTitle>
          <ShippingOption>
            <input
              type="radio"
              id="shipping-email"
              name="shipping"
              value="email"
              checked={shippingOption === 'email'}
              onChange={handleShippingChange}
            />
            <label htmlFor="shipping-email">
              <span>Send via Email</span>
              <span>Free</span>
            </label>
          </ShippingOption>
          <ShippingOption>
            <input
              type="radio"
              id="shipping-post"
              name="shipping"
              value="post"
              checked={shippingOption === 'post'}
              onChange={handleShippingChange}
            />
            <label htmlFor="shipping-post">
              <span>Send via Post</span>
              <span>£2.99</span>
            </label>
          </ShippingOption>
        </ShippingOptionsContainer>
        <SummaryRow className="total">
          <span>Total</span>
          <span>£{total.toFixed(2)}</span>
        </SummaryRow>
        <CheckoutButton to="/checkout">Proceed to Checkout</CheckoutButton>
        <RemoveButton onClick={clearBasket} style={{ marginTop: '1rem', width: '100%' }}>Clear Basket</RemoveButton>
      </SummaryContainer>
    </BasketPageContainer>
  );
};

export default Basket;